import type {ReduxStore} from 'src/reducers';
import type {PageActions, QuestionApiError} from 'src/types/referral-external';
import logger from 'src/utils/logger';
import * as React from 'react';
import {Provider} from 'react-redux';
import {classify} from 'src/utils/classify';
import {getAgencyConfig} from 'src/action-creators/agency';
import {getReleaseFlags} from 'src/action-creators/product-flags';
import {isLanguageSupported} from 'src/utils/language-support';
import {RouterProvider} from 'src/rerouter';
import {browserHistory} from 'src/reroutes/browser-history';
import {ApiError} from 'src/utils/redux-api-v2';
import {
  getQuestions,
  getBrand,
  receiveQuestionsError,
} from 'src/action-creators/referral-external';
import {Layout} from 'src/components/referral-external/layout';
import ErrorBoundary from 'src/components/500/boundary';
import AppRoutes from 'src/reroutes/referral-external';
import I18nContext from 'src/contexts/i18n-context';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import labelI18nInstance, {
  ensureInitialized,
} from 'src/components/lib/i18n/new-i18n';
import 'src/components/app.global.css';
import styles from 'src/components/referral-external/app.css';


export default function ReferralExternalApp({
  referralLinkId,
  action,
  sourceEmail,
  reduxStore,
  uid,
  token,
  iframe,
}: {
  referralLinkId: string;
  action: PageActions | null | undefined;
  sourceEmail: string | null | undefined;
  reduxStore: ReduxStore;
  uid?: string | null | undefined;
  token?: string | null | undefined;
  iframe?: boolean;
}) {
  const params = {
    referralLinkId,
    action,
    sourceEmail,
    uid,
    token,
    iframe,
  } as const;
  const questionApiErrorData: QuestionApiError | null | undefined =
    reduxStore.getState()?.referralExternal?.questionnaire_api_error || null;

  const [i18nState, setI18nState] = React.useState(labelI18nInstance);
  const [i18nReady, setI18nReady] = React.useState(false);

  React.useEffect(() => {
    const initializeI18n = async () => {
      try {
        await ensureInitialized('en'); // ensure translations are initialized
        setI18nReady(true);
      } catch (error: any) {
        console.error('Error initializing translations:', error);
        setI18nReady(true); // allow app to proceed even if translations fail
      }
    };

    initializeI18n();

    const handleLanguageChanged = () => {
      setI18nState({...labelI18nInstance});
    };

    labelI18nInstance.on('languageChanged', handleLanguageChanged);

    return () => {
      labelI18nInstance.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  if (!i18nReady) {
    return (
      <div className={styles.i18nLoader}>
        <CircularLoader size="large" />
      </div>
    );
  }
  return (
    // @ts-expect-error - TS2786 - 'Provider' cannot be used as a JSX component.
    <Provider store={reduxStore}>
      <I18nContext.Provider value={{labelI18nInstance}}>
        <RouterProvider history={browserHistory}>
          <div className={styles.container}>
            <div
              className={classify(styles.body, {
                [styles.errorPageBody]: questionApiErrorData?.status === 410,
                [styles.top]: questionApiErrorData?.status === 404,
              })}
            >
              <ErrorBoundary>
                <Layout isError={!!questionApiErrorData} params={params}>
                  <AppRoutes />
                </Layout>
              </ErrorBoundary>
            </div>
          </div>
        </RouterProvider>
      </I18nContext.Provider>
    </Provider>
  );
}

export function dependencies({dispatch, params: {referralLinkId}}: any): any {
  return Promise.allSettled([
    dispatch(getAgencyConfig()),
    dispatch(getReleaseFlags()),
    dispatch(getBrand(referralLinkId)),
    // @ts-expect-error - TS7006 - Parameter 'error' implicitly has an 'any' type.
    dispatch(getQuestions(referralLinkId)).catch((error) => {
      const {status} = error.response;
      const agency_link = error.responseBody?.agency_link;
      dispatch(receiveQuestionsError({status, agency_link}));
    }),
  ]);
}
